import {
    AirspaceAuthorisationDto,
    AirspaceCheckDto,
    DateFormatService,
    LocationFeatureDto,
    OperationAuthorisation
} from '@flyfreely-portal-ui/flyfreely';
import { feature, featureCollection } from '@turf/helpers';
import { intersect } from '@turf/intersect';
import { Geometry, MultiPolygon, Polygon } from 'geojson';
import moment from 'moment';

/**
 * A function that returns a geometry object suitable for use in the authorisation call command objects.
 * If the passed in flight area is a corridor, i.e. has a GeometryCollection type geometry, the function will strip out only the polygon for the command.
 * @param flightArea the flight area that will be used in the authorisation call
 * @returns a GeoJson.Polygon object
 */
export function findSelectedFlightAreaPolygon(flightArea: LocationFeatureDto) {
    if (flightArea == null || flightArea.geometry == null) {
        return null;
    }
    if (flightArea.geometry.type !== 'GeometryCollection') {
        return flightArea.geometry;
    }
    return flightArea.geometry.geometries.find(g => g.type === 'Polygon');
}

/**
 * Builds the geometry to send as `RequestAirspaceAuthorisationCommand.flightArea`
 * for one or more requested flight areas. A mission with a single flight area
 * is sent as a plain `Polygon` and more than one is combined into a
 * `MultiPolygon`.
 */
export function buildFlightAreaGeometry(
    areas: LocationFeatureDto[]
): Geometry | null {
    const polygons = (areas ?? [])
        .map(a => findSelectedFlightAreaPolygon(a))
        .filter((p): p is Polygon => p != null);
    if (polygons.length === 0) {
        return null;
    }
    if (polygons.length === 1) {
        return polygons[0];
    }
    return {
        type: 'MultiPolygon',
        coordinates: polygons.map(p => p.coordinates)
    };
}

/**
 * Correlates a submitted/returned authorisation's `operatingArea` back to the
 * flight area it was requested for by checking for polygon overlaps.
 */
export function findFlightAreaForOperatingArea(
    operatingArea: Geometry,
    areas: LocationFeatureDto[]
): LocationFeatureDto | null {
    if (
        operatingArea == null ||
        areas == null ||
        (operatingArea.type !== 'Polygon' &&
            operatingArea.type !== 'MultiPolygon')
    ) {
        return null;
    }
    return (
        areas.find(a => {
            const areaPolygon = findSelectedFlightAreaPolygon(a);
            if (
                areaPolygon == null ||
                (areaPolygon.type !== 'Polygon' &&
                    (areaPolygon as Geometry).type !== 'MultiPolygon')
            ) {
                return false;
            }
            try {
                return (
                    intersect(
                        featureCollection([
                            feature(areaPolygon as Polygon | MultiPolygon),
                            feature(operatingArea as Polygon | MultiPolygon)
                        ])
                    ) != null
                );
            } catch {
                // Defensive: turf can throw on degenerate/invalid geometries.
                return false;
            }
        }) ?? null
    );
}

/**
 * Determine if the airspace authorisation is required according to the airspace check result.
 */
export function isAuthorisationRequired(checkResult: AirspaceCheckDto) {
    if (
        checkResult != null &&
        checkResult.availableAutomatedAuthorisationList != null &&
        checkResult.availableAutomatedAuthorisationList.length > 0 &&
        checkResult.ruleOutcomes.some(
            o => o.outcome != null && o.outcome === 'AUTHORISATION_REQUIRED'
        )
    ) {
        return true;
    } else {
        return false;
    }
}

export function formatTime(
    time: string | Date,
    timeZone: string,
    dateFormatService: DateFormatService
) {
    const format = dateFormatService.getDateTimeFormat();
    const localTime =
        timeZone != null
            ? moment(time).tz(timeZone).format(format)
            : moment(time).format(format);
    // If there's no timezone, then indicate that it's UTC
    return timeZone != null ? `${localTime} Local` : `${localTime} UTC`;
}

export function formatAuthorisationTime(time: string | Date, timeZone: string) {
    const localTime =
        timeZone != null
            ? moment(time).tz(timeZone).format('H:mm D MMM YYYY zz')
            : moment(time).format('H:mm D MMM YYYY zz');
    const utc = moment(time).utc().format('YYYY-MM-DD HH:mm');
    return `${localTime} (${utc}Z)`;
}

export function formatCivilTwilight(
    twilighttime: string,
    timeZone: string,
    dateFormatService: DateFormatService
) {
    return twilighttime != null &&
        formatTime(twilighttime, timeZone, dateFormatService) !==
            'Invalid date Local'
        ? formatTime(twilighttime, timeZone, dateFormatService)
        : null;
}

// specialValidationKeys is a list of validation keys with special handlers
// that shouldn't be caught by backup/generic validation.
export const specialValidationKeys = ['remotePilotPhoneNumber'];

/**
 * Returns the first blocking error message for a single `OperationAuthorisation`
 * entry (e.g. one row of a multi-flight-area or multi-window preview), or
 * `null` when it has no blocking errors (i.e. it's OK to submit). Keys in
 * `ignoreKeys` (e.g. `specialValidationKeys`) are treated as non-blocking and
 * skipped.
 */
export function findFirstOperationError(
    operation: OperationAuthorisation,
    ignoreKeys: string[] = []
): string | null {
    const errors = operation?.errors ?? {};
    const key = Object.keys(errors).find(k => !ignoreKeys.includes(k));
    if (key == null) {
        return null;
    }
    return errors[key]?.message ?? 'Invalid';
}

/**
 * Takes the different types of authorisation responses and remaps the LAANC type to one the preview modal can show
 * @param authorisation the result from requesting an authorisation.
 * @returns the authorisation as an AirspaceAuthorisationDto with an authorisationList.
 */
export function mapAuthorisationResult(
    authorisations: OperationAuthorisation[] | AirspaceAuthorisationDto[]
): AirspaceAuthorisationDto[] {
    if (authorisations == null || authorisations.length === 0) {
        return null;
    }
    if (
        authorisations[0].authorisationType ===
        AirspaceAuthorisationDto.AuthorisationType.USA_LAANC
    ) {
        return (<OperationAuthorisation[]>(
            authorisations
        )).map<AirspaceAuthorisationDto>(authorisation => ({
            ...authorisation,
            // @ts-ignore - for now these don't overlap, but the different statuses are handled correctly in the rest of the front end.
            status: authorisation.status
        }));
    } else {
        return <AirspaceAuthorisationDto[]>authorisations;
    }
}

/**
 * A lookup object for airspaces that require the authorisation request to be submitted by the RPIC themselves
 * by matching airspace jurisdiction identifiers to their corresponding authorisation types.
 *
 * The jurisdiction identifiers are hard coded and need to be checked against the actual data.
 */
export const airspaceAuthorisationTypesRequiringCurrentUserAsRpic = {
    ['USA']: AirspaceAuthorisationDto.AuthorisationType.USA_LAANC
};

export const airspaceAuthorisationsRequiringDataManagement = ['USA'];

export const airspaceAuthorisationsRequiringDataManagementDisplayLabels = {
    ['USA']: 'LAANC'
};
